import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Check } from "lucide-react";
import type * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-background self-start",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-neutral-200 text-neutral-900 hover:bg-neutral-300",
        secondary:
          "border-transparent bg-neutral-800 text-neutral-50 hover:bg-neutral-700",
        outline:
          "border border-white/10 bg-neutral-950 text-neutral-50 hover:bg-neutral-900",
        glass:
          "border border-white/30 bg-white/20 backdrop-blur-md text-white hover:bg-white/30",
        destructive:
          "border-transparent bg-red-400/60 text-white hover:bg-red-400/90",
        verified: "border-transparent bg-blue-600 text-white hover:bg-blue-700",
        success:
          "border-transparent bg-lime-400 text-neutral-900 hover:bg-lime-500",
        completed:
          "border border-status-success bg-status-success/80 text-status-success",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

function Badge({
  className,
  variant,
  asChild = false,
  startIcon,
  endIcon,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp className={cn(badgeVariants({ variant }), className)} {...props}>
      {variant === "verified" && <Check className="mr-1 h-3 w-3" />}
      {startIcon && (
        <span className="mr-1 [&>*]:w-3 [&>*]:h-3">{startIcon}</span>
      )}
      {children}
      {endIcon && <span className="ml-1 [&>*]:w-3 [&>*]:h-3">{endIcon}</span>}
    </Comp>
  );
}

export { Badge, badgeVariants };
